import type { Color, DeckCategoryId, ScoreComponentId, SourceKey } from "./types";
import wSymbol from "@/assets/mana-symbols/W.svg";
import uSymbol from "@/assets/mana-symbols/U.svg";
import bSymbol from "@/assets/mana-symbols/B.svg";
import rSymbol from "@/assets/mana-symbols/R.svg";
import gSymbol from "@/assets/mana-symbols/G.svg";
import cSymbol from "@/assets/mana-symbols/C.svg";

export const WUBRG: readonly Color[] = ["W", "U", "B", "R", "G"];

export interface ManaMeta {
  readonly key: SourceKey;
  readonly name: string;
  readonly symbol: string;
  /** Saturated color for glows and chart fills. */
  readonly hex: string;
  /** Lighter tone used for gradients. */
  readonly light: string;
  readonly flavor: string;
}

export const manaMeta: Readonly<Record<SourceKey, ManaMeta>> = {
  W: {
    key: "W",
    name: "White",
    symbol: wSymbol,
    hex: "#f5e7a8",
    light: "#fffbe8",
    flavor: "Order, law, and light",
  },
  U: {
    key: "U",
    name: "Blue",
    symbol: uSymbol,
    hex: "#3d9be9",
    light: "#aae0fa",
    flavor: "Knowledge and deception",
  },
  B: {
    key: "B",
    name: "Black",
    symbol: bSymbol,
    hex: "#8e6f9b",
    light: "#cbc2bf",
    flavor: "Ambition at any cost",
  },
  R: {
    key: "R",
    name: "Red",
    symbol: rSymbol,
    hex: "#ef4a32",
    light: "#f9aa8f",
    flavor: "Freedom and fury",
  },
  G: {
    key: "G",
    name: "Green",
    symbol: gSymbol,
    hex: "#2fa65a",
    light: "#9bd3ae",
    flavor: "Growth and instinct",
  },
  C: {
    key: "C",
    name: "Colorless",
    symbol: cSymbol,
    hex: "#a9a29c",
    light: "#ccc2c0",
    flavor: "Artifice beyond color",
  },
};

/** Guild / shard / wedge names keyed by WUBRG-ordered identity. */
const identityNames: Readonly<Record<string, string>> = {
  "": "Colorless",
  W: "Mono-White",
  U: "Mono-Blue",
  B: "Mono-Black",
  R: "Mono-Red",
  G: "Mono-Green",
  WU: "Azorius",
  UB: "Dimir",
  BR: "Rakdos",
  RG: "Gruul",
  WG: "Selesnya",
  WB: "Orzhov",
  UR: "Izzet",
  BG: "Golgari",
  WR: "Boros",
  UG: "Simic",
  WUB: "Esper",
  UBR: "Grixis",
  BRG: "Jund",
  WRG: "Naya",
  WUG: "Bant",
  WBG: "Abzan",
  WUR: "Jeskai",
  UBG: "Sultai",
  WBR: "Mardu",
  URG: "Temur",
  UBRG: "Glint-Eye",
  WBRG: "Dune-Brood",
  WURG: "Ink-Treader",
  WUBG: "Witch-Maw",
  WUBR: "Yore-Tiller",
  WUBRG: "Five-Color",
};

export function sortColors(colors: readonly string[]): Color[] {
  return WUBRG.filter((color) => colors.includes(color));
}

export function identityName(colors: readonly string[]): string {
  return identityNames[sortColors(colors).join("")] ?? "Unknown";
}

/** Split a Scryfall mana cost like "{2}{U}{U/P}" into symbols. */
export function parseManaCost(cost: string): string[] {
  return [...cost.matchAll(/\{([^}]+)\}/g)].map((match) => match[1] ?? "");
}

export const categoryMeta: Readonly<
  Record<DeckCategoryId, { label: string; color: string; icon: string }>
> = {
  land: { label: "Lands", color: "#a07118", icon: "⛰" },
  ramp: { label: "Ramp", color: "#2fa65a", icon: "🌱" },
  card_draw: { label: "Card Draw", color: "#3d9be9", icon: "📜" },
  removal: { label: "Removal", color: "#ef4a32", icon: "⚔" },
  board_wipe: { label: "Board Wipes", color: "#f97316", icon: "☄" },
  protection: { label: "Protection", color: "#f5e7a8", icon: "🛡" },
  tutor: { label: "Tutors", color: "#94a3b8", icon: "🔮" },
  recursion: { label: "Recursion", color: "#14b8a6", icon: "♻" },
  engine: { label: "Engines", color: "#b87333", icon: "⚙" },
  win_condition: { label: "Win Cons", color: "#e3ad33", icon: "👑" },
  other: { label: "Other", color: "#71717a", icon: "✦" },
};

export const scoreComponentMeta: Readonly<
  Record<ScoreComponentId, { label: string; color: string; description: string }>
> = {
  role: {
    label: "Role",
    color: "#3d9be9",
    description: "Fills a needed deck role",
  },
  strategy: {
    label: "Strategy",
    color: "#c9a26b",
    description: "Fits the commander's strategy",
  },
  mechanic: {
    label: "Mechanic",
    color: "#14b8a6",
    description: "Uses the deck's mechanics",
  },
  win_path: {
    label: "Win Path",
    color: "#e3ad33",
    description: "Contributes to a way to win",
  },
  utility: {
    label: "Utility",
    color: "#a9a29c",
    description: "Generally strong in Commander",
  },
  protected: {
    label: "Protected",
    color: "#f5e7a8",
    description: "Kept by a protected rule",
  },
  synergy: {
    label: "Synergy",
    color: "#2fa65a",
    description: "Pairs well with other cards",
  },
  conflict: {
    label: "Conflict",
    color: "#ef4a32",
    description: "Works against other cards",
  },
  package: {
    label: "Package",
    color: "#ec4899",
    description: "Completes a card package or combo",
  },
  curve: {
    label: "Curve",
    color: "#f97316",
    description: "Distance from the target mana curve",
  },
};
